const millis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const effectiveResultAttemptNumber = (record = {}) =>
  Number.isSafeInteger(record.attemptNumber) && record.attemptNumber >= 1
    ? record.attemptNumber
    : 1;

export const resultAttemptKey = (record = {}) => {
  const attemptId = typeof record.attemptId === "string" ? record.attemptId.trim() : "";
  if (attemptId) return `attempt:${attemptId}`;
  return `legacy:${record.userId || record.studentUid || ""}:${record.quizId || record.quizCode || ""}:1`;
};

export const attemptLabel = (attemptNumber) => `Attempt ${Number(attemptNumber) || 1}`;

export const finalPolicyLabel = (policy) => ({
  highest: "Highest attempt",
  latest: "Latest attempt",
  average: "Average of all attempts",
  first: "First attempt",
}[policy] || "Assessment result");

export const outcomeDisplayLabel = (label) => {
  const value = typeof label === "string" ? label.trim() : "";
  return /^[1-9]$/.test(value) ? `Grade ${value}` : value;
};

export const assessmentSeriesStatus = (attemptStatus, reattempt = null) =>
  (attemptStatus === "completed" || attemptStatus === "expired") &&
  reattempt?.available === true &&
  reattempt?.action === "required"
    ? "reattempt_required"
    : attemptStatus;

export const quizWideSubmissionRecords = (assessmentSeries = []) => {
  const recordsById = new Map();
  assessmentSeries.forEach((series) => {
    (Array.isArray(series?.records) ? series.records : []).forEach((record) => {
      if (record?.id && !recordsById.has(record.id)) recordsById.set(record.id, record);
    });
  });
  return [...recordsById.values()];
};

export const submissionNeedsMarking = (submission = {}) =>
  submission.markAwarded === "Pending" ||
  submission.markAwarded === "N/A" ||
  submission.markAwarded === null ||
  submission.markAwarded === undefined ||
  submission.markAwarded === "";

export const unmarkedSubmissionRecords = (records = []) =>
  records.filter(submissionNeedsMarking);

// The server numbers teacher-facing responses by the order the attempt was
// delivered in (`questionNumber`, null when the question is in neither the
// attempt's recorded order nor the test). Never renumber from the list.
const serverQuestionNumber = (record = {}) =>
  Number.isSafeInteger(record.questionNumber) && record.questionNumber >= 1 ? record.questionNumber : null;

export const orderAttemptRecords = (records = []) => records
  .map((record, index) => ({ record, index, rank: serverQuestionNumber(record) ?? Number.POSITIVE_INFINITY }))
  .sort((left, right) => (left.rank - right.rank) || (left.index - right.index))
  .map(({ record }) => record);

// "Question N" for a record shown at `position` (0-based) in its attempt.
// Responses from servers that predate numbering arrive already ordered, so
// their position is their number.
export const questionNumberLabel = (record = {}, position = 0) => {
  if (serverQuestionNumber(record)) return `Question ${record.questionNumber}`;
  return record.questionNumber === null ? "Question (no longer in this test)" : `Question ${position + 1}`;
};

export const groupResultAttempts = (records = []) => {
  const grouped = new Map();
  records.forEach((record) => {
    const key = resultAttemptKey(record);
    if (!grouped.has(key)) grouped.set(key, {
      id: typeof record.attemptId === "string" && record.attemptId.trim() ? record.attemptId.trim() : key,
      key,
      attemptNumber: effectiveResultAttemptNumber(record),
      legacy: !(typeof record.attemptId === "string" && record.attemptId.trim()),
      records: [],
    });
    grouped.get(key).records.push(record);
  });
  return [...grouped.values()]
    .map((attempt) => ({ ...attempt, records: orderAttemptRecords(attempt.records) }))
    .sort((left, right) => left.attemptNumber - right.attemptNumber || left.key.localeCompare(right.key));
};

export const selectDetailedAttemptId = (attempts = [], finalResult = null, requestedAttemptId = "") => {
  const available = new Set(attempts.map(({ id }) => id));
  if (requestedAttemptId && available.has(requestedAttemptId)) return requestedAttemptId;
  if (finalResult?.selectedAttemptId && available.has(finalResult.selectedAttemptId)) {
    return finalResult.selectedAttemptId;
  }
  const completed = attempts.filter(({ outcome }) => outcome?.status === "complete");
  return [...completed].sort((left, right) => right.attemptNumber - left.attemptNumber)[0]?.id ||
    [...attempts].sort((left, right) => right.attemptNumber - left.attemptNumber)[0]?.id || null;
};

export const attemptProgression = (attempts = []) => {
  const completed = attempts
    .filter(({ outcome }) => outcome?.status === "complete" && Number.isFinite(outcome.percentage))
    .sort((left, right) => left.attemptNumber - right.attemptNumber);
  if (completed.length < 2 || completed[0].attemptNumber !== 1) return null;
  const change = completed.at(-1).outcome.percentage - completed[0].outcome.percentage;
  return {
    change: Math.round((change + Number.EPSILON) * 100) / 100,
    label: `${change > 0 ? "+" : ""}${Math.round((change + Number.EPSILON) * 100) / 100} percentage points`,
  };
};

const completedOutcomeFromRecords = (records = []) => {
  if (!records.length) return null;
  const marks = records.map(({ markAwarded, maxMark }) => ({
    awarded: Number(markAwarded),
    possible: Number(maxMark),
    pending: markAwarded === "Pending" || markAwarded === "N/A",
  }));
  if (marks.some(({ awarded, possible, pending }) =>
    pending || !Number.isFinite(awarded) || !Number.isFinite(possible) || possible < 0
  )) return { status: "pending" };
  const totalAwarded = marks.reduce((sum, { awarded }) => sum + awarded, 0);
  const totalPossible = marks.reduce((sum, { possible }) => sum + possible, 0);
  return {
    status: "complete",
    totalAwarded,
    totalPossible,
    percentage: totalPossible > 0 ? Math.round((totalAwarded / totalPossible) * 10000) / 100 : 0,
    achievedOutcomeId: null,
    achievedOutcomeLabel: null,
  };
};

const assessmentSeriesKey = (studentUid, quizId) => `${studentUid || ""}::${quizId || ""}`;

export const buildAssessmentSeries = ({
  records = [], finalResults = [], attempts = [], seriesStatuses = [],
  defaultStudentUid = "", defaultQuizId = "",
} = {}) => {
  const series = new Map();
  const ensure = (studentUid, quizId) => {
    const key = assessmentSeriesKey(studentUid, quizId);
    if (!series.has(key)) series.set(key, { key, studentUid, quizId, records: [], serverAttempts: [], finalResult: null });
    return series.get(key);
  };

  records.forEach((record) => {
    const studentUid = record.userId || record.studentUid || defaultStudentUid;
    const quizId = record.quizId || defaultQuizId || record.quizCode || "";
    ensure(studentUid, quizId).records.push(record);
  });
  attempts.forEach((attempt) => {
    const studentUid = attempt.studentUid || defaultStudentUid;
    const quizId = attempt.quizId || defaultQuizId;
    if (studentUid && quizId) ensure(studentUid, quizId).serverAttempts.push(attempt);
  });
  finalResults.forEach((entry) => {
    const studentUid = entry.studentUid || defaultStudentUid;
    const quizId = entry.quizId || defaultQuizId;
    if (studentUid && quizId) ensure(studentUid, quizId).finalResult = entry.finalResult || null;
  });
  seriesStatuses.forEach((entry) => {
    const studentUid = entry.studentUid || defaultStudentUid;
    const quizId = entry.quizId || defaultQuizId;
    if (studentUid && quizId) ensure(studentUid, quizId).reattempt = entry;
  });

  return [...series.values()].map((group) => {
    const usedServerAttemptIds = new Set();
    const history = groupResultAttempts(group.records).map((attempt) => {
      let serverAttempt = group.serverAttempts.find(({ id }) => id === attempt.id);
      if (!serverAttempt && attempt.legacy) {
        const compatible = group.serverAttempts.filter((candidate) =>
          candidate.attemptNumber === attempt.attemptNumber &&
          !usedServerAttemptIds.has(candidate.id)
        );
        if (compatible.length === 1) serverAttempt = compatible[0];
      }
      if (serverAttempt) usedServerAttemptIds.add(serverAttempt.id);
      return {
        ...attempt,
        id: serverAttempt?.id || attempt.id,
        key: serverAttempt ? `attempt:${serverAttempt.id}` : attempt.key,
        status: serverAttempt?.status || "submitted",
        expiryReason: serverAttempt?.expiryReason || null,
        startedAt: serverAttempt?.startedAt || null,
        submittedAt: serverAttempt?.submittedAt || attempt.records.map(({ submittedAt }) => submittedAt).find(Boolean) || null,
        outcome: serverAttempt?.attemptOutcome || completedOutcomeFromRecords(attempt.records),
        username: attempt.records[0]?.username || serverAttempt?.username || "",
        email: attempt.records[0]?.email || serverAttempt?.email || "",
      };
    });
    group.serverAttempts.forEach((attempt) => {
      if (!usedServerAttemptIds.has(attempt.id) && !history.some(({ id }) => id === attempt.id)) history.push({
        id: attempt.id,
        key: `attempt:${attempt.id}`,
        attemptNumber: attempt.attemptNumber,
        records: [],
        status: attempt.status,
        expiryReason: attempt.expiryReason || null,
        startedAt: attempt.startedAt || null,
        submittedAt: attempt.submittedAt || null,
        outcome: attempt.attemptOutcome || null,
        username: attempt.username || "",
        email: attempt.email || "",
      });
    });
    history.sort((left, right) => left.attemptNumber - right.attemptNumber || left.id.localeCompare(right.id));
    const latest = history.at(-1) || null;
    const complete = [...history].reverse().find(({ outcome }) => outcome?.status === "complete") || null;
    const finalResult = group.finalResult;
    const summary = finalResult || complete?.outcome || null;
    const representative = group.records.find(({ username }) => typeof username === "string" && username.trim()) ||
      history.find(({ username }) => typeof username === "string" && username.trim()) || {};
    const attemptStatus = latest?.status === "in_progress"
      ? "in_progress"
      : latest && latest.outcome?.status !== "complete" ? "marking" : summary ? "completed" : "pending";
    return {
      ...group,
      attempts: history,
      studentName: representative.username?.trim() || "Student name unavailable",
      studentEmail: representative.email || "",
      selectedAttemptId: selectDetailedAttemptId(history, finalResult),
      summary,
      reattempt: group.reattempt || null,
      displayStatus: assessmentSeriesStatus(attemptStatus, group.reattempt),
    };
  });
};

const seriesKey = (record = {}) =>
  `${record.userId || record.studentUid || ""}::${record.quizId || record.quizCode || ""}`;

export const selectLatestAttemptRecords = (records = []) => {
  const series = new Map();
  records.forEach((record) => {
    const key = seriesKey(record);
    const attemptKey = resultAttemptKey(record);
    if (!series.has(key)) series.set(key, new Map());
    const attempts = series.get(key);
    if (!attempts.has(attemptKey)) attempts.set(attemptKey, []);
    attempts.get(attemptKey).push(record);
  });
  return [...series.values()].flatMap((attempts) => {
    const candidates = [...attempts.values()];
    candidates.sort((left, right) => {
      const numberDifference = effectiveResultAttemptNumber(right[0]) - effectiveResultAttemptNumber(left[0]);
      if (numberDifference) return numberDifference;
      const rightTime = Math.max(...right.map((item) => millis(item.submittedAt)));
      const leftTime = Math.max(...left.map((item) => millis(item.submittedAt)));
      return rightTime - leftTime || resultAttemptKey(right[0]).localeCompare(resultAttemptKey(left[0]));
    });
    return candidates[0] || [];
  });
};
